import { Select, SelectProps, Tooltip } from 'antd';
import FormField from 'components/FormField';
import { useFormikContext } from 'formik';
import { ReactNode, useMemo } from 'react';
import { isToday, useTimeZoneLabel } from 'utils/timeZone';
import { What } from 'views/Marketing/components/PolicyTable';

function TimeZoneField({ required = true }: { required?: boolean }) {
  const { values, initialValues } = useFormikContext<Assistant.Account>();

  const { timeZone, lastTimeZoneUpdateTimeStamp } = values;

  const updated = initialValues.id && values.timeZone !== initialValues.timeZone;

  const showTips = Boolean(
    updated || (lastTimeZoneUpdateTimeStamp && isToday(lastTimeZoneUpdateTimeStamp, timeZone)),
  );

  const { timeZoneRender, t } = useTimeZoneLabel();

  return (
    <FormField<Assistant.Account['timeZone']>
      name="timeZone"
      required={required}
      label={
        <div>
          {t('account_work_timeZone')}
          <Tooltip title={t('account_work_timeZone_tips')}>
            <What />
          </Tooltip>
          {showTips && (
            <span
              style={{
                marginLeft: 12,
                fontSize: 12,
                color: '#F2A100',
              }}
            >
              {t('account_work_timeZone_update_tips', { x: timeZoneRender(timeZone, false) })}
            </span>
          )}
        </div>
      }
      validate={required ? (value) => (value ? undefined : t('请选择')) : undefined}
    >
      {({ field }) => (
        <TimeZoneSelect id="timeZone" allowClear={!required} placeholder={t('请选择')} {...field} />
      )}
    </FormField>
  );
}

function TimeZoneSelect(props: SelectProps<number>) {
  const { timeZoneList, timeZoneRegionsMap, timeZoneRender, t } = useTimeZoneLabel();

  const options = useMemo(() => {
    const { common, others } = timeZoneList.reduce(
      (res, timeZone) => {
        const { common, others } = res;

        const option = (
          <Select.Option key={timeZone} value={Number(timeZone)}>
            {timeZoneRender(timeZone)}
          </Select.Option>
        );

        if (timeZoneRegionsMap[timeZone]) {
          common.push(option);
        } else {
          others.push(option);
        }

        return res;
      },
      {
        common: [] as ReactNode[],
        others: [] as ReactNode[],
      },
    );

    return [
      <Select.OptGroup key="common" label={t('common')}>
        {common}
      </Select.OptGroup>,
      <Select.OptGroup key="others" label={t('aide.其他')}>
        {others}
      </Select.OptGroup>,
    ];
  }, [timeZoneList, timeZoneRegionsMap, timeZoneRender, t]);

  return (
    <Select dropdownMatchSelectWidth={false} {...props}>
      {options}
    </Select>
  );
}

export default TimeZoneField;
